import { FlashbarProps, SelectProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  FoodMenue,
  GetWeeklyFoodMenuesDocument,
  GetWeeklyFoodMenuesQuery,
  useCreateWeeklyFoodMenueMutation,
} from '../../../../generated/graphql';
import { ErrorText } from '../../../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

export const schema = yup
  .object()
  .shape({
    title: yup.string().required(ErrorText.Required),
    price: yup
      .string()
      .matches(/^\d{1,},\d{2}$/, ErrorText.Currency)
      .required(ErrorText.Required),
    displayday: yup.object().required(ErrorText.Required),
    foodtype: yup.object().required(ErrorText.Required),
  })
  .required();

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
  additiveOptions: SelectProps.Option[];
  allergenOptions: SelectProps.Option[];
};

export type FormValues = {
  title: string;
  price: string;
  displayday: SelectProps.Option | number;
  foodtype: SelectProps.Option;
  subtitle?: string;
  is_active?: boolean;
  productinformation?: SelectProps.Option[];
};

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
  additiveOptions,
  allergenOptions,
}: CreateProps) {
  const params = useParams();
  const [createWeeklyFoodMenue, { loading: createLoading }] =
    useCreateWeeklyFoodMenueMutation();
  const methods = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {},
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createWeeklyFoodMenue({
      variables: { input: { ...entry, week: Number(params.week ?? 0) } },
      update: (cache, { data }) => {
        const addData = data?.createWeeklyFoodMenue;
        const existingData = cache.readQuery({
          query: GetWeeklyFoodMenuesDocument,
          variables: {
            Filter: {
              week: {
                eq: Number(params.week),
              },
            },
          },
        }) as GetWeeklyFoodMenuesQuery;
        const oldData = existingData.listWeeklyFoodMenues?.items as FoodMenue[];

        cache.writeQuery({
          query: GetWeeklyFoodMenuesDocument,
          variables: {
            Filter: {
              week: {
                eq: Number(params.week),
              },
            },
          },
          data: {
            listWeeklyFoodMenues: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createWeeklyFoodMenue;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.title}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form
          additiveOptions={additiveOptions}
          allergenOptions={allergenOptions}
        />
      </Modal>
    </FormProvider>
  );
}
